import { Controller } from "@hotwired/stimulus"

// Theme toggle controller — manages dark/light mode + background
// Persists to localStorage for instant application across Turbo navigations
export default class extends Controller {
  static targets = ["icon"]

  connect() {
    this.applyTheme()
    this._turboHandler = () => this._applyBackground()
    document.addEventListener("turbo:load", this._turboHandler)
    document.addEventListener("turbo:render", this._turboHandler)
  }

  disconnect() {
    if (this._turboHandler) {
      document.removeEventListener("turbo:load", this._turboHandler)
      document.removeEventListener("turbo:render", this._turboHandler)
    }
  }

  toggle() {
    const isDark = document.documentElement.classList.contains("dark")
    if (isDark) {
      document.documentElement.classList.remove("dark")
      localStorage.setItem("mod-theme", "light")
    } else {
      document.documentElement.classList.add("dark")
      localStorage.setItem("mod-theme", "dark")
    }
    this._applyBackground()
    this.updateIcon()
  }

  applyTheme() {
    const saved = localStorage.getItem("mod-theme")
    if (saved === "dark") {
      document.documentElement.classList.add("dark")
    } else {
      document.documentElement.classList.remove("dark")
    }
    this._syncFromMeta()
    this._applyBackground()
    this.updateIcon()
  }

  // Sync localStorage from meta tags (server is source of truth on full loads)
  _syncFromMeta() {
    const imgMeta = document.querySelector('meta[name="user-bg-image"]')
    const colorMeta = document.querySelector('meta[name="user-bg-color"]')
    const darkMeta = document.querySelector('meta[name="user-bg-color-dark"]')

    if (imgMeta && imgMeta.content) {
      localStorage.setItem("mod-bg-type", "image")
      localStorage.setItem("mod-bg-image", imgMeta.content)
      localStorage.removeItem("mod-bg-color")
      localStorage.removeItem("mod-bg-color-dark")
    } else if (colorMeta && colorMeta.content) {
      localStorage.setItem("mod-bg-type", "color")
      localStorage.setItem("mod-bg-color", colorMeta.content)
      localStorage.setItem("mod-bg-color-dark", darkMeta ? darkMeta.content : colorMeta.content)
      localStorage.removeItem("mod-bg-image")
    } else {
      localStorage.removeItem("mod-bg-type")
      localStorage.removeItem("mod-bg-color")
      localStorage.removeItem("mod-bg-color-dark")
      localStorage.removeItem("mod-bg-image")
    }
  }

  // Apply background from localStorage (fast, reliable)
  _applyBackground() {
    const isDark = document.documentElement.classList.contains("dark")
    const bgType = localStorage.getItem("mod-bg-type")
    const html = document.documentElement

    if (bgType === "image") {
      const imgUrl = localStorage.getItem("mod-bg-image")
      if (imgUrl) {
        const fallback = isDark ? '#1a1714' : '#f5f2ed'
        html.style.background = `${fallback} url(${imgUrl}) center/cover fixed no-repeat`
        html.classList.add("has-bg")
        return
      }
    }

    if (bgType === "color") {
      const light = localStorage.getItem("mod-bg-color")
      const dark = localStorage.getItem("mod-bg-color-dark")
      if (light) {
        html.style.background = isDark ? (dark || light) : light
        html.classList.add("has-bg")
        return
      }
    }

    // Default — no custom background
    html.style.background = isDark ? "#1a1714" : "#f5f2ed"
    html.classList.remove("has-bg")
  }

  updateIcon() {
    if (!this.hasIconTarget) return
    const isDark = document.documentElement.classList.contains("dark")
    this.iconTarget.innerHTML = isDark
      ? `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>`
      : `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/></svg>`
  }
}
